import { LLMConfig, ChatMessage } from '../../../types/llm'
import { LLMProviderInterface } from '../llm-service'

export class CohereProvider implements LLMProviderInterface {
  private config: LLMConfig
  private baseUrl: string

  constructor(config: LLMConfig) {
    this.config = config
    this.baseUrl = config.baseUrl || 'https://api.cohere.ai/v1'
  }

  private formatMessages(messages: ChatMessage[]): { message: string; chat_history: any[] } {
    const chatHistory: any[] = []
    let currentMessage = ''

    for (let i = 0; i < messages.length; i++) {
      const msg = messages[i]
      
      if (i === messages.length - 1 && msg.role === 'user') {
        // Last message should be the current query
        currentMessage = msg.content
      } else {
        // Previous messages go to chat history
        chatHistory.push({
          role: msg.role === 'user' ? 'USER' : 'CHATBOT',
          message: msg.content
        })
      }
    }

    return {
      message: currentMessage,
      chat_history: chatHistory
    }
  }

  async chat(messages: ChatMessage[], config?: Partial<LLMConfig>): Promise<string> {
    const { message, chat_history } = this.formatMessages(messages)

    const response = await fetch(`${this.baseUrl}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.config.apiKey}`
      },
      body: JSON.stringify({
        model: config?.model || this.config.model,
        message,
        chat_history,
        temperature: config?.temperature || this.config.temperature || 0.7,
        max_tokens: config?.maxTokens || this.config.maxTokens || 2000
      })
    })

    if (!response.ok) {
      const error = await response.text()
      throw new Error(`Cohere API error: ${error}`)
    }

    const data = await response.json() as any
    return data.text
  }

  async streamChat(
    messages: ChatMessage[],
    config?: Partial<LLMConfig>,
    callbacks?: {
      onToken?: (token: string) => void
      onComplete?: (fullText: string) => void
      onError?: (error: Error) => void
    }
  ): Promise<void> {
    try {
      const { message, chat_history } = this.formatMessages(messages)

      const response = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.config.apiKey}`
        },
        body: JSON.stringify({
          model: config?.model || this.config.model,
          message,
          chat_history,
          temperature: config?.temperature || this.config.temperature || 0.7,
          max_tokens: config?.maxTokens || this.config.maxTokens || 2000,
          stream: true
        })
      })

      if (!response.ok) {
        const error = await response.text()
        throw new Error(`Cohere API error: ${error}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response body')

      const decoder = new TextDecoder()
      let fullText = ''

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n')

        for (const line of lines) {
          if (line.trim()) {
            try {
              const parsed = JSON.parse(line)
              if (parsed.event_type === 'text-generation') {
                const content = parsed.text
                if (content) {
                  fullText += content
                  callbacks?.onToken?.(content)
                }
              } else if (parsed.event_type === 'stream-end') {
                callbacks?.onComplete?.(fullText)
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } catch (error) {
      callbacks?.onError?.(error as Error)
      throw error
    }
  }
}
